import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { useMemo } from 'react'
import { TruncatedText } from '@/components/commons/truncated-text'
import { Avatar } from '@/components/ui/avatar'
import { DataTable } from '@/components/ui/data-table'
import { Skeleton } from '@/components/ui/skeleton'
import { TableScrollBox } from '@/features/marquee/components/table-scroll-box'
import {
	TableEmpty,
	TableError
} from '@/features/marquee/components/table-states'
import { TeamMemberRowMenu } from '@/features/marquee/components/team-member-row-menu'
import { TeamMemberStatusBadge } from '@/features/marquee/components/team-member-status-badge'
import { useMinWidth } from '@/features/marquee/hooks/use-min-width'
import { operatorQueryOptions } from '@/features/marquee/query-options'
import {
	operatorRoleToLabelKey,
	type TeamRowAction,
	teamStatus
} from '@/features/marquee/utils'
import type { MarqueeManagedOperator } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

const SKELETON_KEYS = ['s1', 's2', 's3', 's4', 's5']

interface TeamTableProps {
	data: MarqueeManagedOperator[]
	isLoading?: boolean
	isError?: boolean
	onAction: (action: TeamRowAction, member: MarqueeManagedOperator) => void
}

/**
 * The Team roster: one row per operator, already filtered by the page.
 *
 * Desktop renders the virtualized `DataTable` inside the card's scroll box;
 * below `l` every row becomes a card, the same split the export preview makes.
 */
export function TeamTable({
	data,
	isLoading,
	isError,
	onAction
}: TeamTableProps) {
	const { t } = useTranslation()
	const isDesktop = useMinWidth('l')

	const columns = useMemo(
		() => [
			{
				id: 'member' as const,
				header: t('marquee:team_col_member'),
				cell: ({ dataItem }: { dataItem: MarqueeManagedOperator }) => (
					<MemberCell member={dataItem} />
				),
				size: 300
			},
			{
				id: 'role' as const,
				header: t('marquee:team_col_role'),
				cell: ({ dataItem }: { dataItem: MarqueeManagedOperator }) => (
					<RolePill member={dataItem} />
				),
				size: 160
			},
			{
				id: 'marketManager' as const,
				header: t('marquee:team_col_market_manager'),
				cell: ({ dataItem }: { dataItem: MarqueeManagedOperator }) => (
					<span className='font-medium text-grey-1000 text-sm/4.5'>
						{t(
							dataItem.is_market_manager
								? 'marquee:team_yes'
								: 'marquee:team_no'
						)}
					</span>
				),
				size: 170
			},
			{
				id: 'sfUserId' as const,
				header: t('marquee:team_col_sf_user_id'),
				cell: ({ dataItem }: { dataItem: MarqueeManagedOperator }) => (
					<TruncatedText className='font-medium text-grey-1000 text-sm/4.5 tabular-nums'>
						{sfUserIdCell(dataItem)}
					</TruncatedText>
				),
				size: 180
			},
			{
				id: 'status' as const,
				header: t('marquee:team_col_status'),
				cell: ({ dataItem }: { dataItem: MarqueeManagedOperator }) => (
					<TeamMemberStatusBadge status={teamStatus(dataItem)} />
				),
				size: 160
			},
			{
				id: 'lastActive' as const,
				header: t('marquee:team_col_last_active'),
				cell: ({ dataItem }: { dataItem: MarqueeManagedOperator }) => (
					<TruncatedText className='font-medium text-grey-1000 text-sm/4.5'>
						{LastActive({ row: dataItem })}
					</TruncatedText>
				),
				size: 150
			},
			{
				id: 'actions' as const,
				header: null,
				headerAlign: 'end' as const,
				cell: ({ dataItem }: { dataItem: MarqueeManagedOperator }) => (
					<div className='flex w-full items-center justify-end'>
						<TeamMemberRowMenu
							member={dataItem}
							onAction={onAction}
						/>
					</div>
				),
				size: 72
			}
		],
		[t, onAction]
	)

	if (isError) {
		return (
			<TableError
				description='marquee:team_error_description'
				title='marquee:team_error_title'
			/>
		)
	}

	if (!isDesktop) {
		if (isLoading) {
			return (
				<div className='flex flex-col gap-3'>
					{SKELETON_KEYS.map((key) => (
						<Skeleton height={140} key={key} />
					))}
				</div>
			)
		}
		if (data.length === 0) {
			return (
				<TableEmpty
					description='marquee:team_empty_description'
					title='marquee:team_empty_title'
				/>
			)
		}
		return (
			<div className='flex flex-col gap-3'>
				{data.map((member) => (
					<TeamMemberCard
						key={member.id}
						member={member}
						onAction={onAction}
					/>
				))}
			</div>
		)
	}

	return (
		<TableScrollBox>
			{(scrollParent) => (
				<div className='**:data-[slot=table-header-gap-row]:h-px **:data-[slot=table-header-gap-row]:bg-grey-300'>
					<DataTable
						columnPinning={{ left: [], right: ['actions'] }}
						columns={columns}
						customScrollParent={scrollParent}
						data={data}
						emptyComponent={
							<TableEmpty
								description='marquee:team_empty_description'
								title='marquee:team_empty_title'
							/>
						}
						isLoading={isLoading}
					/>
				</div>
			)}
		</TableScrollBox>
	)
}

/** The id only matters on a Market Manager: anyone else shows a dash. */
const sfUserIdCell = (row: MarqueeManagedOperator) =>
	(row.is_market_manager && row.sf_user_id) || '-'

/**
 * Relative to now ("about 2 hours ago"). An invite nobody has accepted reads
 * "Pending" rather than a dash: there is no last activity because there has
 * been no first one.
 */
function LastActive({ row }: { row: MarqueeManagedOperator }) {
	if (row.last_active_at) {
		return formatDistanceToNow(row.last_active_at, { addSuffix: true })
	}
	return '-'
}

function MemberCell({ member }: { member: MarqueeManagedOperator }) {
	const { t } = useTranslation()
	const { data: operator } = useQuery(operatorQueryOptions())
	const isOwnRow = member.id === operator?.id

	return (
		<div className='flex min-w-0 items-center gap-8'>
			<Avatar
				className='size-12 font-medium text-grey-1000 text-sm'
				imageUrl=''
				name={member.name ?? member.email}
			/>
			<div className='flex min-w-0 flex-col gap-1'>
				<TruncatedText className='font-medium text-grey-1000 text-sm/4.5'>
					{isOwnRow
						? t('marquee:team_member_you', {
								name: member.name ?? member.email
							})
						: (member.name ?? member.email)}
				</TruncatedText>
				<TruncatedText className='text-grey-700 text-sm/4.5'>
					{member.email}
				</TruncatedText>
			</div>
		</div>
	)
}

function RolePill({ member }: { member: MarqueeManagedOperator }) {
	const { t } = useTranslation()
	return (
		<span className='inline-flex items-center whitespace-nowrap rounded-full bg-grey-200 px-3 py-1.5 font-medium text-grey-1000 text-xs/6'>
			{t(operatorRoleToLabelKey[member.role])}
		</span>
	)
}

function TeamMemberCard({
	member,
	onAction
}: {
	member: MarqueeManagedOperator
	onAction: (action: TeamRowAction, member: MarqueeManagedOperator) => void
}) {
	const { t } = useTranslation()
	return (
		<div className='flex flex-col gap-3 rounded-2xl bg-neutral-white p-4 shadow-airbnb-3'>
			<div className='flex items-start justify-between gap-3'>
				<MemberCell member={member} />
				<TeamMemberRowMenu member={member} onAction={onAction} />
			</div>
			<dl className='flex flex-col gap-1.5 text-sm'>
				<CardRow label={t('marquee:team_col_role')}>
					<RolePill member={member} />
				</CardRow>
				<CardRow label={t('marquee:team_col_status')}>
					<TeamMemberStatusBadge status={teamStatus(member)} />
				</CardRow>
				<CardRow label={t('marquee:team_col_market_manager')}>
					{t(
						member.is_market_manager
							? 'marquee:team_yes'
							: 'marquee:team_no'
					)}
				</CardRow>
				<CardRow label={t('marquee:team_col_sf_user_id')}>
					{sfUserIdCell(member)}
				</CardRow>
				<CardRow label={t('marquee:team_col_last_active')}>
					<LastActive row={member} />
				</CardRow>
			</dl>
		</div>
	)
}

function CardRow({
	label,
	children
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<div className='flex items-center justify-between gap-4'>
			<dt className='shrink-0 text-neutral-500'>{label}</dt>
			<dd className='wrap-break-word flex min-w-0 justify-end text-right text-neutral-900'>
				{children}
			</dd>
		</div>
	)
}
